import {inject, Injectable, NgZone, OnDestroy, signal} from '@angular/core';
import {Database, onValue, ref, Unsubscribe, update} from 'firebase/database';
import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	Firestore,
	getDocs,
	limit,
	orderBy,
	query,
	QueryDocumentSnapshot,
	where
} from 'firebase/firestore';
import {FirebaseInitService} from '../shared/firebase-init.service';
import {Mention, Message, Room} from '@models';
import {PrivateMessageNotification} from '@models';
import {NotificationService} from './notification.service';
import {ProfileService} from '../community';
import {SendmailService} from './sendmail.service';
import {ErrorHandlerService} from '../shared';
import {OnboardingCommandService} from '../engagement';

/**
 * Interface pour stocker les informations d'un listener Firebase
 */
interface FirebaseListener {
	unsubscribe: Unsubscribe;
	roomUuid: string;
}

/**
 * Service de gestion des messages dans les salles de chat.
 *
 * Ce service gère :
 * - La récupération des messages par salle
 * - L'observation en temps réel des nouveaux messages
 * - L'envoi de messages
 * - La suppression de messages
 * - La gestion des mentions (@username)
 *
 * @example
 * ```typescript
 * // Récupérer les messages d'une salle (signal)
 * const messages = this.messageService.getMessagesByRoom(room);
 * effect(() => {
 *   console.log(messages());
 * });
 *
 * // Observer les nouveaux messages (signal)
 * const latest = this.messageService.getMessageObserver(room);
 * effect(() => {
 *   console.log('Nouveau message:', latest());
 * });
 * ```
 */
@Injectable()
export class MessageService implements OnDestroy {
	private readonly profileService: ProfileService = inject(ProfileService);
	private readonly notificationService: NotificationService = inject(NotificationService);
	private readonly sendMailService: SendmailService = inject(SendmailService);
	private readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);
	private readonly ngZone: NgZone = inject(NgZone);
	private readonly onboardingCommandService: OnboardingCommandService = inject(OnboardingCommandService);

	private readonly firebaseInit = inject(FirebaseInitService);

	private get database(): Database | null {
		try {
			return this.firebaseInit.database;
		} catch {
			// Database non disponible (ex: dans les tests)
			return null;
		}
	}

	private get firestore(): Firestore | null {
		try {
			return this.firebaseInit.firestore;
		} catch {
			// Firestore non disponible (ex: dans les tests)
			return null;
		}
	}

	/**
	 * Cache des messages synchronisés par salle
	 */
	private readonly syncMessages: Map<string, Message[]> = new Map<string, Message[]>();

	/**
	 * Messages par salle (signal)
	 */
	private readonly roomMessages: Map<string, ReturnType<typeof signal<Message[]>>> = new Map();

	/**
	 * Dernier message par salle (signal)
	 */
	private readonly roomObservers: Map<string, ReturnType<typeof signal<Message | null>>> = new Map();

	/**
	 * Listeners Firebase actifs pour le nettoyage
	 */
	private readonly firebaseListeners: Map<string, FirebaseListener> = new Map<string, FirebaseListener>();

	/**
	 * Dernier uuid reçu par room pour tracer les changements
	 */
	private readonly lastMessageIds: Map<string, string> = new Map();


	/**
	 * Récupère les messages d'une salle de chat.
	 *
	 * @param room - La salle de chat
	 * @param _unusedStartAt - Date optionnelle pour la pagination (non implémentée actuellement)
	 * @returns Signal émettant un tableau de messages
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	getMessagesByRoom(room: Room, _unusedStartAt?: Date) {
		const messageSignal = this.getRoomMessagesSignal(room.uuid);

		if (this.syncMessages.has(room.uuid)) {
			// Utiliser les messages en cache
			const cachedMessages = this.syncMessages.get(room.uuid);
			if (cachedMessages) {
				// Utiliser queueMicrotask au lieu de setTimeout pour de meilleures performances
				queueMicrotask(() => {
					const sortedMessages = [...cachedMessages].sort(
						(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
					).reverse();
					messageSignal.set(sortedMessages);
				});
			}
		} else {
			// Charger les messages depuis Firestore
			this.loadMessagesFromFirestore(room.uuid);
		}

		return messageSignal.asReadonly();
	}

	async hasUserSentMessage(roomUuid: string, userUuid: string, currentMessageId?: string): Promise<boolean | null> {
		if (!this.firestore) {
			return null;
		}
		try {
			const messagesRef = collection(this.firestore, `roomMessages/${roomUuid}/messages`);
			const messagesQuery = query(messagesRef, where('sender', '==', userUuid), limit(2));
			const snapshot = await getDocs(messagesQuery);
			if (snapshot.empty) {
				return false;
			}
			if (snapshot.size >= 2) {
				return true;
			}
			const onlyMessageId = snapshot.docs[0]?.id ?? '';
			return !(currentMessageId && onlyMessageId === currentMessageId);

		} catch (error) {
			this.errorHandler.handleErrorSilently(error);
			return null;
		}
	}

	/**
	 * Charge les messages depuis Firestore.
	 *
	 * @param roomUuid - UUID de la salle
	 * @param roomUuid - UUID de la salle
	 */
	private loadMessagesFromFirestore(roomUuid: string): void {
		if (!this.firestore) {
			return;
		}
		const messagesRef = collection(this.firestore, `roomMessages/${roomUuid}/messages`);
		const messagesQuery = query(messagesRef, orderBy('createdAt', 'desc'), limit(30));
		const messageSignal = this.getRoomMessagesSignal(roomUuid);

		getDocs(messagesQuery)
			.then((snapshot) => {
				const messages: Message[] = [];
				snapshot.forEach((docSnapshot: QueryDocumentSnapshot) => {
					const data = docSnapshot.data();
					messages.push(
						new Message({
							...data,
							uuid: docSnapshot.id
						})
					);
				});
				this.ngZone.run(() => {
					const existingMessages = this.syncMessages.get(roomUuid) ?? [];
					const mergedMap = new Map<string, Message>();
					messages.forEach((message) => mergedMap.set(message.uuid, message));
					existingMessages.forEach((message) => {
						if (!mergedMap.has(message.uuid)) {
							mergedMap.set(message.uuid, message);
						}
					});
					const mergedMessages = [...mergedMap.values()];
					this.syncMessages.set(roomUuid, mergedMessages);
					messageSignal.set(mergedMessages);
				});
			})
			.catch((error) => {
				this.errorHandler.handleError(error);
			});
	}

	/**
	 * Observe les nouveaux messages en temps réel pour une salle.
	 *
	 * @param room - La salle de chat
	 * @returns Signal émettant les nouveaux messages
	 */
	getMessageObserver(room: Room) {
		if (this.roomObservers.has(room.uuid)) {
			return this.roomObservers.get(room.uuid)!.asReadonly();
		}
		const roomObserver = signal<Message | null>(null);
		this.roomObservers.set(room.uuid, roomObserver);
		const listenerKey = `message_observer_${room.uuid}`;

		// Nettoyer le listener précédent s'il existe
		this.cleanupListener(listenerKey);

		if (!this.database) {
			return roomObserver.asReadonly();
		}

		const messageRef = ref(this.database, `rooms_last_message/${room.uuid}/last_message`);
		const unsubscribe = onValue(
			messageRef,
			(snapshot) => {
				this.ngZone.run(() => {
					if (!snapshot.exists()) {
						return;
					}

					const messageData = snapshot.val();
					const messageUuid = messageData?.uuid ?? '';
					const previousUuid = this.lastMessageIds.get(room.uuid);
					if (messageUuid && messageUuid !== previousUuid) {
						this.lastMessageIds.set(room.uuid, messageUuid);
					}
					const roomMessages = this.syncMessages.get(room.uuid) ?? [];

					// Vérifier si le message n'existe pas déjà
					const messageExists = roomMessages.some((m: Message) => m.uuid === messageData.uuid);
					const isNotFromCurrentUser = messageData.sender !== this.profileService.profile()?.uuid;

					if (!messageExists && isNotFromCurrentUser) {
						const newMessage = new Message({
							...messageData,
							uuid: messageData.uuid || ''
						});
						roomMessages.push(newMessage);
						this.syncMessages.set(room.uuid, roomMessages);
						this.getRoomMessagesSignal(room.uuid).set([...roomMessages]);
						roomObserver.set(newMessage);
					}
				});
			},
			(error) => {
				this.errorHandler.handleError(error);
			}
		);

		// Stocker le listener pour le nettoyage
		this.firebaseListeners.set(listenerKey, {
			unsubscribe,
			roomUuid: room.uuid
		});

		return roomObserver.asReadonly();
	}

	/**
	 * Envoie un message dans une salle de chat.
	 *
	 * @param room - La salle de chat
	 * @param message - Le message à envoyer
	 * @returns Promise résolue avec l'ID du message créé
	 */
	async sendMessage(room: Room, message: Message): Promise<string> {
		try {
			if (!this.firestore) {
				throw new Error('Firestore is not available');
			}
			if (!this.database) {
				throw new Error('Database is not available');
			}

			const createdAt = new Date();
			const createdAtMs = createdAt.getTime();
			message.createdAt = createdAt;
			const messageData = {
				content: message.content,
				createdAt: createdAtMs,
				isImage: message.isImage,
				sender: message.sender,
				fileUrl: message.fileUrl || ''
			};

			const localUuid = message.uuid || `local_${createdAt}_${Math.random().toString(36).slice(2, 8)}`;
			const localMessage = new Message({
				...messageData,
				uuid: localUuid
			});
			this.upsertLocalMessage(room.uuid, localMessage);

			// Gérer les notifications pour les messages privés
			if (room.isPrivate) {
				await this.handlePrivateMessageNotification(room, message);
			}

			// Ajouter le message à Firestore
			const docRef = await addDoc(
				collection(this.firestore, `roomMessages/${room.uuid}/messages`),
				messageData
			);

			this.replaceLocalMessageUuid(room.uuid, localUuid, docRef.id, createdAtMs);

			// Mettre à jour le dernier message dans Realtime Database
			await update(
				ref(this.database, `rooms_last_message/${room.uuid}/last_message`),
				{
					...messageData,
					uuid: docRef.id
				}
			);

			if (room.isPrivate) {
				await this.onboardingCommandService.completeFirstInteraction('message');
			}

			// Gérer les mentions (@username)
			if (message.content && message.content.includes('@')) {
				await this.handleMentions(room, message.content);
			}

			return docRef.id;
		} catch (error) {
			this.errorHandler.handleError(error);
			throw error;
		}
	}

	/**
	 * Gère les notifications pour les messages privés.
	 *
	 * @param room - La salle de chat
	 * @param message - Le message envoyé
	 */
	private async handlePrivateMessageNotification(room: Room, message: Message): Promise<void> {
		try {
			const recipientUid = room.members.find((uid) => uid !== message.sender);
			if (!recipientUid) {
				return;
			}

			const newMention = new PrivateMessageNotification();
			newMention.set(room);
			newMention.uuid = '';

			const profile = await this.profileService.getProfileByUid(recipientUid);
			if (profile?.uuid) {
				this.notificationService.sendNotification('privateMessage', profile, newMention);
				this.sendMailService.sendNewMessageToUser(profile);
			}
		} catch (error) {
			this.errorHandler.handleError(error, false); // Ne pas afficher de toast pour les erreurs de notification
		}
	}

	/**
	 * Gère les mentions (@username) dans un message.
	 *
	 * @param room - La salle de chat
	 * @param content - Le contenu du message
	 */
	private async handleMentions(room: Room, content: string): Promise<void> {
		const mentions = content.split('@').slice(1); // Ignorer la première partie avant le premier @

		for (const mentionPart of mentions) {
			const username = mentionPart.split(' ')[0].trim();
			if (!username) {
				continue;
			}

			const profile = await this.profileService.getProfileByUsername(username);
			if (profile) {
				const newMention = new Mention();
				newMention.set(room);
				newMention.uuid = '';
				this.notificationService.sendNotification('mention', profile, newMention);
			}
		}
	}

	/**
	 * Supprime un message d'une salle de chat.
	 *
	 * @param message - Le message à supprimer
	 * @param selectedRoom - La salle contenant le message
	 */
	async deleteMessage(message: Message, selectedRoom: Room): Promise<void> {
		try {
			if (!this.firestore) {
				throw new Error('Firestore is not available');
			}
			await deleteDoc(
				doc(this.firestore, `roomMessages/${selectedRoom.uuid}/messages/${message.uuid}`)
			);
		} catch (error) {
			this.errorHandler.handleError(error);
			throw error;
		}
	}

	private getRoomMessagesSignal(roomUuid: string) {
		if (!this.roomMessages.has(roomUuid)) {
			this.roomMessages.set(roomUuid, signal<Message[]>([]));
		}
		return this.roomMessages.get(roomUuid)!;
	}

	private upsertLocalMessage(roomUuid: string, message: Message): void {
		const roomMessages = this.syncMessages.get(roomUuid) ?? [];
		const messageIndex = roomMessages.findIndex((m) => m.uuid === message.uuid);
		if (messageIndex > -1) {
			roomMessages[messageIndex] = message;
		} else {
			roomMessages.push(message);
		}
		this.syncMessages.set(roomUuid, roomMessages);
		this.getRoomMessagesSignal(roomUuid).set([...roomMessages]);
	}

	private replaceLocalMessageUuid(roomUuid: string, localUuid: string, realUuid: string, createdAtMs: number): void {
		const roomMessages = this.syncMessages.get(roomUuid) ?? [];
		const messageIndex = roomMessages.findIndex((m) => m.uuid === localUuid);
		if (messageIndex > -1) {
			roomMessages[messageIndex].uuid = realUuid;
			roomMessages[messageIndex].createdAt = new Date(createdAtMs);
			this.syncMessages.set(roomUuid, roomMessages);
			this.getRoomMessagesSignal(roomUuid).set([...roomMessages]);
		}
	}

	/**
	 * Nettoie un listener Firebase spécifique.
	 *
	 * @param listenerKey - Clé du listener à nettoyer
	 */
	private cleanupListener(listenerKey: string): void {
		const listener = this.firebaseListeners.get(listenerKey);
		if (listener) {
			listener.unsubscribe();
			this.firebaseListeners.delete(listenerKey);
		}
	}

	/**
	 * Nettoie tous les listeners d'une salle spécifique.
	 *
	 * @param roomUuid - UUID de la salle
	 */
	cleanupRoomListeners(roomUuid: string): void {
		for (const [key, listener] of this.firebaseListeners.entries()) {
			if (listener.roomUuid === roomUuid) {
				listener.unsubscribe();
				this.firebaseListeners.delete(key);
			}
		}
	}

	/**
	 * Nettoie toutes les ressources lors de la destruction du service.
	 */
	ngOnDestroy(): void {
		// Nettoyer tous les listeners Firebase
		for (const listener of this.firebaseListeners.values()) {
			listener.unsubscribe();
		}
		this.firebaseListeners.clear();
		this.roomMessages.clear();
		this.roomObservers.clear();
	}
}
